import { useEffect } from "react";
import { tenantDisplayName } from "../utils/tenantProfile";

const labelValue = (value) => value || "—";
const yesNo = (value) => value ? "Yes" : "No";
const titleCase = (value) => value
  ? value.toLowerCase().split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
  : "—";
const formatDate = (value) => value
  ? new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value))
  : "—";

function Details({ entries }) {
  return (
    <dl className="admin-mailbox-details">
      {entries.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function MailboxProfileModal({ mailbox, onClose }) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const tenant = mailbox.tenant;

  return (
    <div
      className="admin-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="admin-modal admin-modal-wide" role="dialog" aria-modal="true" aria-labelledby="mailbox-profile-title">
        <div className="admin-modal-header">
          <div>
            <p className="admin-page-eyebrow">Mailbox profile</p>
            <h2 id="mailbox-profile-title">Mailbox #{mailbox.number}</h2>
            <p>Mailbox configuration and current tenant assignment.</p>
          </div>
          <button className="admin-modal-close" type="button" aria-label="Close mailbox profile" onClick={onClose}>&times;</button>
        </div>
        <div className="admin-modal-body admin-profile-sections">
          <section className="admin-profile-section">
            <h3>Mailbox details</h3>
            <Details entries={[
              ["Number", mailbox.number],
              ["Occupancy", tenant ? "Occupied" : "Available"],
              ["Status", titleCase(mailbox.status)],
              ["Size", titleCase(mailbox.size)],
              ["Has mail letters", yesNo(mailbox.hasMail)],
              ["Has UPS/FedEx package", yesNo(mailbox.hasPackage)],
              ["Location", labelValue(mailbox.location)],
              ["Notes", labelValue(mailbox.notes)],
              ["Assigned", formatDate(mailbox.assignedAt)],
              ["Last mail received", formatDate(mailbox.lastMailAt)],
              ["Last notification", formatDate(mailbox.lastNotifiedAt)],
              ["Created", formatDate(mailbox.createdAt)],
              ["Updated", formatDate(mailbox.updatedAt)],
            ]} />
          </section>
          <section className="admin-profile-section">
            <h3>Assigned tenant</h3>
            {tenant ? (
              <Details entries={[
                ["Name", tenantDisplayName(tenant)],
                ["Business", labelValue(tenant.businessName)],
                ["Email", tenant.email],
                ["Alternate email", labelValue(tenant.alternateEmail)],
                ["Mobile phone", labelValue(tenant.mobilePhone)],
                ["Business phone", labelValue(tenant.businessPhone)],
                ["Home phone", labelValue(tenant.homePhone)],
                ["Address", labelValue([tenant.streetAddress, tenant.city, tenant.state, tenant.zipCode].filter(Boolean).join(", "))],
                ["Unit number", labelValue(tenant.unitNumber)],
                ["Building access key", labelValue(tenant.buildingAccessKeyNumber)],
                ["Tenant status", titleCase(tenant.status)],
                ["Notes", labelValue(tenant.notes)],
                ["Move-in date", formatDate(tenant.moveInDate)],
                ["Move-out date", formatDate(tenant.moveOutDate)],
              ]} />
            ) : <p className="admin-empty-state">No tenant is assigned to this mailbox.</p>}
          </section>
        </div>
        <div className="admin-modal-footer">
          <button className="admin-button admin-button-primary" type="button" onClick={onClose}>Close</button>
        </div>
      </section>
    </div>
  );
}
